import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { postsAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import AdminLayout from './AdminLayout';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Calendar,
  MoreVertical
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const AdminPostsPage = () => {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const data = await postsAPI.getAll();
      setPosts(data);
    } catch (error) {
      toast.error('Failed to fetch posts');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await postsAPI.delete(deleteId);
      setPosts(posts.filter(p => p.id !== deleteId));
      toast.success('Post deleted');
    } catch (error) {
      toast.error('Failed to delete post');
    } finally {
      setDeleteId(null);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(lang === 'en' ? 'en-GB' : 'ro-RO', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getLocalizedTitle = (post) => {
    return post[`title_${lang}`] || post.title_ro || post.title_en || 'Untitled';
  };

  const filteredPosts = posts.filter((post) => {
    const title = getLocalizedTitle(post).toLowerCase();
    return title.includes(searchTerm.toLowerCase());
  });

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-stone-800">{t('admin.posts')}</h1>
          <p className="text-stone-500 text-sm">{posts.length} total posts</p>
        </div>
        <Link to="/admin/posts/new">
          <Button className="bg-islamic-teal hover:bg-islamic-teal-light gap-2" data-testid="new-post-btn">
            <Plus className="w-4 h-4" />
            {t('admin.createPost')}
          </Button>
        </Link>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            type="text"
            placeholder={t('common.search') + '...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-posts"
          />
        </div>
      </div>

      {/* Posts Table */}
      <Card className="border-stone-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8">
              <div className="animate-pulse space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-16 bg-stone-200 rounded"></div>
                ))}
              </div>
            </div>
          ) : filteredPosts.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-stone-200 bg-stone-50">
                    <th className="text-left text-xs font-medium text-stone-500 uppercase px-4 py-3">Title</th>
                    <th className="text-left text-xs font-medium text-stone-500 uppercase px-4 py-3 hidden md:table-cell">Category</th>
                    <th className="text-left text-xs font-medium text-stone-500 uppercase px-4 py-3 hidden sm:table-cell">Status</th>
                    <th className="text-left text-xs font-medium text-stone-500 uppercase px-4 py-3 hidden lg:table-cell">Date</th>
                    <th className="text-right text-xs font-medium text-stone-500 uppercase px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {filteredPosts.map((post) => (
                    <tr key={post.id} className="hover:bg-stone-50" data-testid={`post-row-${post.id}`}>
                      <td className="px-4 py-4">
                        <div>
                          <p className="font-medium text-stone-800 line-clamp-1">{getLocalizedTitle(post)}</p>
                          <p className="text-xs text-stone-500 mt-1">{post.author_name}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4 hidden md:table-cell">
                        <span className="px-2 py-1 bg-stone-100 text-stone-600 text-xs rounded capitalize">
                          {post.category}
                        </span>
                      </td>
                      <td className="px-4 py-4 hidden sm:table-cell">
                        <span className={`px-2 py-1 text-xs rounded ${
                          post.status === 'published' 
                            ? 'bg-teal-100 text-teal-700' 
                            : 'bg-stone-100 text-stone-600'
                        }`}>
                          {post.status}
                        </span>
                      </td>
                      <td className="px-4 py-4 hidden lg:table-cell">
                        <span className="text-sm text-stone-500 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(post.created_at)}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`post-actions-${post.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <Link to={`/annonces/${post.id}`} target="_blank">
                                <Eye className="w-4 h-4 mr-2" /> {t('common.view')}
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <Link to={`/admin/posts/${post.id}`}>
                                <Edit className="w-4 h-4 mr-2" /> {t('common.edit')}
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => setDeleteId(post.id)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> {t('common.delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center">
              <p className="text-stone-500 mb-4">{t('common.noResults')}</p>
              <Link to="/admin/posts/new">
                <Button className="bg-islamic-teal hover:bg-islamic-teal-light gap-2">
                  <Plus className="w-4 h-4" />
                  {t('admin.createPost')}
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.confirm')}</AlertDialogTitle>
            <AlertDialogDescription>
              {lang === 'en' 
                ? 'Are you sure you want to delete this post? This action cannot be undone.'
                : 'Ești sigur că vrei să ștergi acest anunț? Această acțiune nu poate fi anulată.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminPostsPage;
